import * as Dialog from "@radix-ui/react-dialog";

import styled from "styled-components";
import { Button } from "./ui/button";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Countdown } from "./CountDown";
import LoadingIndicator from "./LoadingIndicator";
import { useEffect, useState } from "react";

import ConfirmTradeOTPModal from "./ConfirmTradeOTPModal";
import {
  useAcceptB2bTradeMutation,
  useB2bOrders,
  useConfirmBusinessTradeMutationApi,
} from "src/hooks/useB2bTrading";
import { getSettlementTimeline, resolveCollectionRouteLabel, resolveFlowCopy } from "src/helpers/tradeFlow";

const TradeOrderDetailsModal = ({
  open,
  handleClose,
  details,
  isBusiness,
  businessId,
}: {
  open: boolean;
  handleClose: () => void;
  details: any;
  isBusiness?: boolean;
  businessId?: any;
}) => {
  const { confirmB2bTrade } = useConfirmBusinessTradeMutationApi();
  const acceptB2bTrade = useAcceptB2bTradeMutation();
  const { refetch: refetchBusinessOrders } = useB2bOrders({
    businessId: businessId,
  });

  const [showOTPModal, setShowOTPModal] = useState<any>(false);

  function handleConfirmTrade(id: string) {
    if (isBusiness) {
      confirmB2bTrade.mutate({ orderId: id, businessId: businessId });
    }
  }

  const handleAcceptTrade = (id: string, accept: boolean) => {
    if (isBusiness) {
      acceptB2bTrade.mutate({
        order_id: id,
        accept,
        business_id: businessId,
      });
    }
    handleClose();
  };

  useEffect(() => {
    if (confirmB2bTrade.isSuccess) {
      refetchBusinessOrders();
      handleClose();
      if (confirmB2bTrade.data?.verification_required) {
        setShowOTPModal(true);
      }
    }
  }, [confirmB2bTrade.isSuccess, confirmB2bTrade.data, refetchBusinessOrders, handleClose]);

  useEffect(() => {
    if (acceptB2bTrade.isSuccess) {
      refetchBusinessOrders();
      handleClose();
    }
  }, [acceptB2bTrade.isSuccess, refetchBusinessOrders, handleClose]);

  return (
    <>
      <ConfirmTradeOTPModal
        open={showOTPModal}
        handleClose={() => setShowOTPModal(false)}
        id={details?.id}
        businessId={businessId}
      />
      <Dialog.Root open={open}>
        <Dialog.Portal>
          <Dialog.Overlay className="DialogOverlay" />
          <DialogContent className="DialogContentSmall">
            <Dialog.Title className="DialogTitle">Trade Order Details</Dialog.Title>
            {confirmB2bTrade.isPending ? (
              <div className="flex items-center justify-center min-h-80">
                <LoadingIndicator />
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", marginTop: 25 }}>
                  <FlowSummaryCard>
                    <FlowHeadline>{resolveFlowCopy(details).label}</FlowHeadline>
                    <FlowDescription>{resolveFlowCopy(details).description}</FlowDescription>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      <FlowChip>{resolveCollectionRouteLabel(details)}</FlowChip>
                      <FlowChip>Buyer receives {details?.buyer_asset_currency || "AKL"}</FlowChip>
                      <FlowChip>Seller receives {details?.seller_settlement_currency || details?.currency_code}</FlowChip>
                    </div>
                  </FlowSummaryCard>

                  <TimelineContainer>
                    {getSettlementTimeline(details).map((step) => (
                      <TimelineRow key={step.title}>
                        <TimelineDot $state={step.state} />
                        <div>
                          <TimelineTitle>{step.title}</TimelineTitle>
                          <TimelineText>{step.description}</TimelineText>
                        </div>
                      </TimelineRow>
                    ))}
                  </TimelineContainer>

                  <Row>
                    <Item>Amount</Item>
                    <Item>{details?.amount}</Item>
                  </Row>

                  <Row>
                    <Item>Lumi amount</Item>
                    <Item>{details?.amount_in_lumi}</Item>
                  </Row>

                  <Row>
                    <Item>Convertion Rate</Item>
                    <Item>{details?.conversion_rate_usd}</Item>
                  </Row>

                  <Row>
                    <Item>Currency</Item>
                    <Item>{details?.currency_code}</Item>
                  </Row>

                  <Row>
                    <Item>Payment method</Item>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "250px",
                      }}
                    >
                      <Item>{details?.payment_method?.payment_option.name}</Item>
                      <Item>{details?.payment_method?.account_details.provider_name}</Item>
                      <Item>{details?.payment_method?.account_details.account_number}</Item>
                      <Item>{details?.payment_method?.account_details.name}</Item>
                    </div>
                  </Row>

                  <Row>
                    <Item>Status</Item>
                    <Item>{resolveFlowCopy(details).label}</Item>
                  </Row>

                  <Row>
                    <Item>Collection rail</Item>
                    <Item>{resolveCollectionRouteLabel(details)}</Item>
                  </Row>

                  <Row>
                    <Item>Buyer receives</Item>
                    <Item>{details?.buyer_asset_currency || "AKL"}</Item>
                  </Row>

                  <Row>
                    <Item>Seller receives</Item>
                    <Item>{details?.seller_settlement_currency || details?.currency_code}</Item>
                  </Row>
                  <Row>
                    <Item>Note</Item>
                    <Item>{details?.note}</Item>
                  </Row>

                  {details?.provider_payment && (
                    <Row>
                      <Item>Provider payment</Item>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "250px",
                        }}
                      >
                        <Item>{details?.provider_payment?.provider}</Item>
                        <Item>{details?.provider_payment?.status || "Pending"}</Item>
                        <Item>{details?.provider_payment?.transaction_reference}</Item>
                        <Item>{details?.provider_payment?.message}</Item>
                      </div>
                    </Row>
                  )}

                  <Row>
                    <Item>Timer</Item>
                    {details.status === "Pending" ? (
                      <Countdown endTime={details.expires_at} />
                    ) : (
                      "Time's up!"
                    )}
                  </Row>
                </div>
                {isBusiness && details?.status === "Pending" &&
                !details?.is_accepted &&
                !details?.is_initiator ? (
                  <Footer>
                    <StyledButton
                      onClick={() => handleAcceptTrade(details?.id, true)}
                      type="submit"
                      disabled={confirmB2bTrade.isPending}
                      className="flex items-center justify-center py-2 mr-3 rounded-md bg-red-500 px-6 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                    >
                      Accept
                    </StyledButton>
                    <Dialog.Close asChild>
                      <StyledButton
                        onClick={() => handleAcceptTrade(details?.id, false)}
                        type="submit"
                        disabled={confirmB2bTrade.isPending}
                        className="flex items-center justify-center py-2 rounded-md bg-gray-500 px-6 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                      >
                        Decline
                      </StyledButton>
                    </Dialog.Close>
                  </Footer>
                ) : isBusiness && details?.status === "Pending" ? (
                  <div style={{ display: "flex", marginTop: 25, justifyContent: "flex-end" }}>
                    <Dialog.Close asChild>
                      <StyledButton
                        onClick={() => handleConfirmTrade(details?.id)}
                        type="submit"
                        disabled={
                          !details?.is_accepted ||
                          (details?.trade_role === "SELLER" &&
                            details?.confirmed_by_buyer === false) ||
                          (details?.trade_role === "BUYER" && details?.confirmed_by_buyer === true)
                        }
                        className="flex items-center justify-center py-2 rounded-md bg-red-500 px-6 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                      >
                        Confirm Trade
                      </StyledButton>
                    </Dialog.Close>
                  </div>
                ) : null}
              </>
            )}
            <Dialog.Close asChild>
              <CloseIconButton className="IconButton" aria-label="Close" onClick={handleClose}>
                <Cross2Icon width="20" height="20" />
              </CloseIconButton>
            </Dialog.Close>
          </DialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

const CloseIconButton = styled.button`
  @media print {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  @media print {
    display: none;
  }
`;

const DialogContent = styled(Dialog.Content)`
  @media print {
    width: 100vw;
    heigt: 100vh;
  }
`;

const FlowSummaryCard = styled.div`
  border: 1px solid #f1d7d2;
  background: linear-gradient(135deg, #fff7f4 0%, #ffffff 100%);
  border-radius: 18px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const FlowHeadline = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const FlowDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #64748b;
  margin-top: 0.35rem;
`;

const FlowChip = styled.span`
  border: 1px solid #f0d8d2;
  background: #fff;
  color: #9a3412;
  border-radius: 9999px;
  padding: 0.35rem 0.75rem;
`;

const TimelineContainer = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #fff;
`;

const TimelineRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.55rem 0;
`;

const TimelineDot = styled.div<{ $state?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  margin-top: 0.3rem;
  background: ${({ $state: state }) =>
    state === "complete"
      ? "#16a34a"
      : state === "current"
        ? "#ea580c"
        : state === "failed"
          ? "#dc2626"
          : "#cbd5e1"};
`;

const TimelineTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: #111827;
`;

const TimelineText = styled.div`
  font-size: 0.84rem;
  color: #64748b;
  line-height: 1.5;
  margin-top: 0.2rem;
`;

const Row = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
  width: 100%;
  padding: 0.5rem;
`;

const Item = styled.div`
  width: 50%;
`;

const Footer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: 1rem;
`;
export default TradeOrderDetailsModal;
